/* Public-facing Privacy Policy. Linked from the buyer signup form's
   credit-share consent checkbox and the platform footer. Plain HTML —
   intentionally no API round-trip so it's instant + indexable.

   IMPORTANT: this is a sensible engineering baseline drafted in plain
   English. Run the wording past actual legal counsel before going live
   with paid customers — see /legal/trade-score for the same
   caveat and a fintech-specific disclosure. */
import { Link } from "react-router-dom";
import { ShieldCheck, ArrowLeft } from "lucide-react";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="legal-privacy">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/" className="text-xs text-foreground/55 hover:text-foreground inline-flex items-center gap-1.5 mb-8">
          <ArrowLeft className="w-3 h-3"/>Back to verqo
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-foreground text-accent grid place-items-center">
            <ShieldCheck className="w-5 h-5"/>
          </div>
          <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/55">Legal · verqo Wholesale OS</div>
        </div>
        <h1 className="display text-4xl tracking-tight mt-2">Privacy Policy</h1>
        <div className="text-[12px] text-foreground/55 mt-2">v1.0 · Effective May 1, 2026</div>

        <div className="mt-8 prose prose-sm max-w-none text-foreground/85 leading-relaxed">
          <p>
            verqo ("we", "us") provides a B2B wholesale operating system that connects
            sellers (brands) and buyers (retailers) so they can transact on
            credit terms with embedded insurance, payments, and shipping. This
            Privacy Policy explains what personal and business data we collect,
            why we collect it, how we use it, and the rights you have over it.
          </p>

          <h2 className="display text-xl mt-8 mb-2">1. What we collect</h2>
          <ul>
            <li><strong>Account data:</strong> name, business email, phone, role (buyer/seller).</li>
            <li><strong>Business identity (KYB):</strong> legal business name, EIN, address, business type, year founded — we verify this through Middesk.</li>
            <li><strong>Beneficial owner identity (KYC):</strong> legal name, date of birth, last 4 of SSN (hashed at rest, never round-tripped). Required by USA PATRIOT Act §326 before our banking partner activates a wallet.</li>
            <li><strong>Banking signals:</strong> read-only account, transaction, and identity data via Plaid — only used to underwrite credit.</li>
            <li><strong>Trade insurance:</strong> we share your business name, EIN, and country with Allianz Trade so they can grade your creditworthiness and underwrite cover for your sellers.</li>
            <li><strong>Transactional data:</strong> orders, invoices, payments, shipments, claims, support tickets.</li>
            <li><strong>Technical:</strong> IP address, user-agent, request logs (rotated; used for security + abuse detection).</li>
          </ul>

          <h2 className="display text-xl mt-8 mb-2">2. Why we collect it</h2>
          <ul>
            <li>Verify you are who you say you are (regulatory + fraud prevention).</li>
            <li>Underwrite credit limits and offer trade credit insurance.</li>
            <li>Process orders, payments, shipping, and returns.</li>
            <li>Provide customer support and improve the product.</li>
            <li>Comply with legal obligations (KYC/KYB, tax reporting, anti-money-laundering).</li>
          </ul>

          <h2 className="display text-xl mt-8 mb-2">3. Who we share it with</h2>
          <ul>
            <li><strong>Sellers you transact with:</strong> when you complete signup as a buyer, you authorize us to share your <strong>Verqo Trade Score</strong> (an informational descriptive signal — <em>not</em> a credit decision) with sellers you connect with — <Link to="/legal/trade-score" className="underline">read the full Trade Score methodology &amp; disclosures</Link>.</li>
            <li><strong>Service providers (sub-processors):</strong> Middesk (KYB), Plaid (banking), Allianz Trade (insurance), Resend (email), Verqo Freight Network (shipping), Modern Treasury (payments), Stripe (cards), AWS / Google Cloud (hosting).</li>
            <li><strong>Government / regulators:</strong> when required by law or valid legal process.</li>
            <li>We <strong>do not</strong> sell your data to advertisers or data brokers.</li>
          </ul>

          <h2 className="display text-xl mt-8 mb-2">4. How long we keep it</h2>
          <p>
            We keep account, KYB, and transaction data for the lifetime of your
            account plus 7 years after closure (federal tax + AML retention
            minimums). Adverse-action notices, audit logs, and consent records are
            retained for 7 years. You can request deletion of personally
            identifiable data outside the regulatory retention window at any
            time — see Section 6.
          </p>

          <h2 className="display text-xl mt-8 mb-2">5. Security</h2>
          <p>
            All data is transmitted over TLS 1.2+. Passwords and SSN-last-4 are
            stored as bcrypt hashes. Database access is restricted to authorised
            engineers with audited credentials. We log every privileged action.
            We use Sentry for error monitoring and rotate secrets on a
            scheduled cadence.
          </p>

          <h2 className="display text-xl mt-8 mb-2">6. Your rights</h2>
          <ul>
            <li><strong>Access:</strong> request a copy of every data point we hold on you.</li>
            <li><strong>Correction:</strong> ask us to fix inaccurate data.</li>
            <li><strong>Deletion:</strong> ask us to delete data outside the 7-year retention window.</li>
            <li><strong>Portability:</strong> export your account data in a structured JSON format.</li>
            <li><strong>Dispute / opt-out:</strong> the Verqo Trade Score we share with sellers is informational only and is NOT a credit decision under FCRA. You may dispute factual errors or opt out of seller-facing display at any time (see the <Link to="/legal/trade-score" className="underline">Trade Score methodology</Link>).</li>
            <li><strong>Withdraw consent:</strong> revoke consent for future processing — note this may end your ability to receive credit terms.</li>
          </ul>
          <p>Email <a href="mailto:privacy@verqo.com" className="underline">privacy@verqo.com</a> to exercise any of these rights. We respond within 30 days.</p>

          <h2 className="display text-xl mt-8 mb-2">7. International transfers</h2>
          <p>
            We are headquartered in the United States. If you are in the EU,
            UK, or another region with cross-border-transfer rules, your data
            is transferred to the US under Standard Contractual Clauses (or an
            equivalent legal mechanism).
          </p>

          <h2 className="display text-xl mt-8 mb-2">8. Changes</h2>
          <p>
            We will email you and post a notice in verqo at least 14 days
            before any material change to this policy.
          </p>

          <h2 className="display text-xl mt-8 mb-2">9. Contact</h2>
          <p>
            Questions? Email <a href="mailto:privacy@verqo.com" className="underline">privacy@verqo.com</a> or
            open a ticket inside verqo from any portal.
          </p>
        </div>
      </div>
    </div>
  );
}
